import type { UserProfile } from '@/lib/supabase';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type User = UserProfile;

// Interface for integration partner access token (auth-specific)
export interface IntegrationToken {
  token: string;
  expiresIn: string;
  expiresAt: number; // Unix timestamp when token expires
  partnerId: string; // Which integration partner this token is for
  createdAt: number; // Unix timestamp when token was created
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  integrationToken: IntegrationToken | null; // Integration partner access token
  sessionValidation: {
    isValidating: boolean;
    lastValidated?: string; // ISO timestamp
    isValid?: boolean;
    error?: string;
  };
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  integrationToken: null,
  sessionValidation: {
    isValidating: false,
  },
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Set user data and authentication status
    setUser(state, action: PayloadAction<User>) {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.loading = false;
    },

    // Update loading state
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },

    // Clear session and reset to initial state
    clearSession(state) {
      state.user = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.integrationToken = null; // Clear integration token on logout
      state.sessionValidation = {
        isValidating: false,
      };
    },

    // Set integration partner access token
    setIntegrationToken(state, action: PayloadAction<IntegrationToken>) {
      state.integrationToken = action.payload;
    },

    // Clear integration token
    clearIntegrationToken(state) {
      state.integrationToken = null;
    },

    // Set session validation state
    setSessionValidation(
      state,
      action: PayloadAction<Partial<AuthState['sessionValidation']>>
    ) {
      state.sessionValidation = {
        ...state.sessionValidation,
        ...action.payload,
      };
    },

    // Initialize auth state (for persistence restoration)
    initializeAuth(state, action: PayloadAction<Partial<AuthState>>) {
      return { ...state, ...action.payload, loading: false };
    },
  },
});

export const {
  setUser,
  setLoading,
  clearSession,
  setIntegrationToken,
  clearIntegrationToken,
  setSessionValidation,
  initializeAuth,
} = authSlice.actions;

export default authSlice.reducer;
